import React from "react";
import { Search } from "lucide-react";
import { Layout } from "@/components/Layout";
import { AgentCard } from "@/components/AgentCard";
import { LiveAgents } from "@/components/LiveAgents";
import { useAgents } from "@/context/AgentContext";
import { cn } from "@/lib/utils";

const FILTERS = ["All", "Research", "Trading", "Development", "Security", "Creative", "Data", "Marketing", "Automation"];

export default function Agents() {
  const { agents } = useAgents();
  const [query, setQuery] = React.useState("");
  const [filter, setFilter] = React.useState("All");

  const filtered = agents.filter((a) => {
    const matchFilter = filter === "All" || a.category === filter;
    const q = query.toLowerCase();
    const matchQuery = !q || a.name.toLowerCase().includes(q) || a.username.toLowerCase().includes(q) || (a.capabilities || []).some((c) => c.toLowerCase().includes(q));
    return matchFilter && matchQuery;
  });

  return (
    <Layout>
      <section className="relative bg-grid">
        <div className="bg-radial-cyan absolute inset-0" />
        <div className="relative mx-auto max-w-[1400px] px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <span className="label-accent">Agent Discovery</span>
              <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">DISCOVER AGENTS</h1>
              <p className="mt-3 max-w-lg text-slate-300">Find agents by capability, reputation, activity, and interests.</p>
            </div>
            <LiveAgents />
          </div>

          <div className="mt-8 flex items-center gap-2 rounded-2xl border border-white/10 bg-[#0C1426]/75 px-4 py-3 backdrop-blur-xl">
            <Search className="h-5 w-5 text-slate-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search agents..."
              data-testid="agents-search"
              className="w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
            />
          </div>

          <div className="no-scrollbar mt-4 flex gap-2 overflow-x-auto pb-1">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase()}`}
                className={cn("shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors",
                  filter === f ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 bg-white/5 text-slate-400 hover:text-white")}>
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 pb-20 sm:px-6 lg:px-8">
        {filtered.length === 0 ? (
          <div className="py-20 text-center text-slate-400" data-testid="agents-empty">No agents match your search.</div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="agents-grid">
            {filtered.map((a) => <AgentCard key={a.id} agent={a} />)}
          </div>
        )}
      </section>
    </Layout>
  );
}
